import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";

/**
 * Luxury visitor counter — increments once per browser session.
 * Renders as illuminated gold numerals over a hairline ledger.
 */
export default function VisitorCounter() {
  const [count, setCount] = useState(null);
  const [display, setDisplay] = useState(0);
  const incremented = useRef(false);
  const animated = useRef(false);

  useEffect(() => {
    if (incremented.current) return; // StrictMode double-mount guard
    incremented.current = true;
    api.post("/visits")
      .then(({ data }) => setCount(data.count))
      .catch(() => setCount(0));
  }, []);

  // Animated count-up
  useEffect(() => {
    if (count == null) return;
    if (animated.current) { setDisplay(count); return; }
    animated.current = true;
    const start = performance.now();
    const dur = 1400;
    const from = 0;
    let raf;
    const step = (t) => {
      const k = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - k, 3);
      setDisplay(Math.round(from + (count - from) * eased));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [count]);

  const formatted = (display ?? 0).toLocaleString("en-US").padStart(6, "0");

  return (
    <div data-testid="visitor-counter" className="flex items-center gap-5 select-none">
      <div className="flex items-center gap-2">
        <span className="relative inline-flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-gold opacity-70 animate-ping" style={{ background: "var(--color-gold)" }} />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: "var(--color-gold)" }} />
        </span>
        <span className="text-[10px] uppercase tracking-[0.35em] text-muted-fg">Patrons received</span>
      </div>
      <div className="flex items-baseline gap-0">
        {formatted.split("").map((ch, i) => (
          <span
            key={i}
            className={`font-display text-2xl gold-text tabular-nums inline-flex items-center justify-center w-5 ${ch === "," ? "w-2" : ""}`}
            style={{ letterSpacing: "0.04em" }}
          >
            {ch}
          </span>
        ))}
      </div>
    </div>
  );
}
